/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import { isDesktop, isMobile } from 'react-device-detect';
import { IconButton, Slider } from '@mui/material';
import {
  PlayArrowRounded,
  PauseRounded,
  RestartAltRounded,
  Replay10Rounded,
  Forward10Rounded,
  FastRewindRounded,
  FastForwardRounded,
  ShuffleRounded,
  LoopRounded,
  VolumeUpRounded,
  VolumeMuteRounded,
} from '@mui/icons-material';
import { usePlayer } from '@/infrastructure/context/player-context';
import { formatTime, formatVolume } from '@/infrastructure/functions/player';
import s from './styles.module.scss';

const AppPlayer: React.FC = () => {
  const { player, setPlaying, setEnded, nextTrack, prevTrack, setRandomTrack } =
    usePlayer();

  const { t: tButtons } = useTranslation('buttons');

  const [playerState, setPlayerState] = useState({
    playing: false,
    muted: false,
    shuffle: false,
    loop: false,
    currentTime: 0,
    duration: 0,
    volume: 1,
  });

  const [playerRef, setAudioRef] = useState<HTMLAudioElement | null>(null);

  const playBtnClassNames = classNames(s['btn'], s['active']);

  const shuffleBtnClassNames = classNames(s['btn'], {
    [s['active']]: playerState.shuffle,
  });
  const loopBtnClassNames = classNames(s['btn'], {
    [s['active']]: playerState.loop,
  });

  const handlePlay = () => {
    if (playerRef) {
      setPlaying(true);
      setPlayerState({ ...playerState, playing: true });
      playerRef.play().catch((e) => {
        if (e) {
          setPlaying(false);
          setPlayerState({ ...playerState, playing: false });
        }
      });
    }
  };
  const handlePause = () => {
    const isEnded =
      Math.ceil(playerState.duration) === Math.ceil(playerState.currentTime);
    if (playerRef && !isEnded) {
      playerRef.pause();
      setPlaying(false);
      setPlayerState({ ...playerState, playing: false });
    }
  };
  const handleToggle = () => {
    if (playerState.playing) handlePause();
    else handlePlay();
  };
  const handleNext = () => {
    setEnded(true);
    if (playerState.shuffle) setRandomTrack();
    else nextTrack();
    setTimeout(() => {
      setEnded(false);
    }, 500);
  };
  const handlePrev = () => {
    setEnded(true);
    prevTrack();
    setTimeout(() => {
      setEnded(false);
    }, 500);
  };
  const handleShuffle = () => {
    setPlayerState({ ...playerState, shuffle: !playerState.shuffle });
  };
  const handleLoop = () => {
    setPlayerState({ ...playerState, loop: !playerState.loop });
  };
  const handleVolume = (newValue: number) => {
    if (playerRef) {
      setPlayerState({ ...playerState, muted: false, volume: newValue });
      playerRef.volume = newValue;
      playerRef.muted = false;
    }
  };
  const handleMute = () => {
    if (playerRef) {
      const muted = !playerRef.muted;
      const volume = muted ? 0 : playerRef.volume;
      setPlayerState({ ...playerState, volume, muted });
      playerRef.muted = muted;
    }
  };
  const handleTime = (newValue: number) => {
    if (playerRef) {
      playerRef.currentTime = Number(newValue);
    }
  };
  const handleRestart = () => {
    if (playerRef) {
      handleTime(0);
      handlePlay();
    }
  };
  const handleRewind = () => {
    if (playerRef) {
      const rewindTime = playerRef.currentTime - 10;
      handleTime(rewindTime);
    }
  };
  const handleFoward = () => {
    if (playerRef) {
      const fowardTime = playerRef.currentTime + 10;
      handleTime(fowardTime);
    }
  };

  const onEnded = () => {
    setEnded(true);
    if (playerState.loop) handleRestart();
    if (!playerState.loop && playerState.shuffle) setRandomTrack();
    if (!playerState.loop && !playerState.shuffle) nextTrack();
    setTimeout(() => {
      setEnded(false);
    }, 500);
  };

  const onTimeUpdate = () => {
    if (playerRef) {
      setPlayerState({
        ...playerState,
        currentTime: playerRef.currentTime,
      });
    }
  };

  const onLoadedMetadata = () => {
    if (playerRef) {
      setPlayerState({
        ...playerState,
        duration: playerRef.duration,
      });
    }
  };

  useEffect(() => {
    if (player.playing && player.currentTrack) {
      handlePlay();
    } else {
      handlePause();
    }
  }, [player.playing, player.currentTrack, playerRef]);

  return (
    <div className={s['player']}>
      <div className={s['controls']}>
        {isMobile && (
          <IconButton
            className={s['btn']}
            onClick={handleMute}
            aria-label={
              playerState.muted || !playerState.volume
                ? tButtons('unmute')
                : tButtons('mute')
            }
            title={
              playerState.muted || !playerState.volume
                ? tButtons('unmute')
                : tButtons('mute')
            }
          >
            {playerState.muted || !playerState.volume ? (
              <VolumeMuteRounded fontSize="inherit" />
            ) : (
              <VolumeUpRounded fontSize="inherit" />
            )}
          </IconButton>
        )}
        <IconButton
          title={tButtons('restart')}
          size="small"
          className={s['btn']}
          onClick={handleRestart}
        >
          <RestartAltRounded fontSize="inherit" />
        </IconButton>
        <IconButton
          title={tButtons('rewind10')}
          size="small"
          className={s['btn']}
          onClick={handleRewind}
        >
          <Replay10Rounded fontSize="inherit" />
        </IconButton>
        <IconButton
          title={tButtons('playPrew')}
          size="small"
          className={s['btn']}
          onClick={handlePrev}
        >
          <FastRewindRounded fontSize="small" />
        </IconButton>
        <IconButton
          title={playerState.playing ? tButtons('pause') : tButtons('play')}
          size="large"
          className={playBtnClassNames}
          onClick={handleToggle}
        >
          {playerState.playing ? (
            <PauseRounded fontSize="large" />
          ) : (
            <PlayArrowRounded fontSize="large" />
          )}
        </IconButton>
        <IconButton
          title={tButtons('playNext')}
          size="small"
          className={s['btn']}
          onClick={handleNext}
        >
          <FastForwardRounded fontSize="small" />
        </IconButton>
        <IconButton
          title={tButtons('forward10')}
          size="small"
          className={s['btn']}
          onClick={handleFoward}
        >
          <Forward10Rounded fontSize="inherit" />
        </IconButton>
        <IconButton
          title={tButtons('shuffle')}
          size="small"
          className={shuffleBtnClassNames}
          onClick={handleShuffle}
        >
          <ShuffleRounded fontSize="inherit" />
        </IconButton>
        <IconButton
          title={tButtons('loop')}
          size="small"
          className={loopBtnClassNames}
          onClick={handleLoop}
        >
          <LoopRounded fontSize="inherit" />
        </IconButton>
      </div>
      <div className={s['progress-bar']}>
        <div className={s['time']}>
          <span>{formatTime(playerState.currentTime)}</span>
          <span>{formatTime(playerState.duration)}</span>
        </div>
        <Slider
          size="small"
          color="secondary"
          valueLabelDisplay="auto"
          aria-label={tButtons('timestamp')}
          title={tButtons('timestamp')}
          value={playerState.currentTime}
          max={playerState.duration}
          classes={{
            root: s['slider'],
            thumb: s['thumb'],
          }}
          valueLabelFormat={(value) => `${formatTime(value)}`}
          onChange={(e, newValue) => handleTime(Number(newValue))}
        />
      </div>
      {isDesktop && (
        <div className={s['volume']}>
          <IconButton
            className={s['btn']}
            onClick={handleMute}
            aria-label={
              playerState.muted || !playerState.volume
                ? tButtons('unmute')
                : tButtons('mute')
            }
            title={
              playerState.muted || !playerState.volume
                ? tButtons('unmute')
                : tButtons('mute')
            }
          >
            {playerState.muted || !playerState.volume ? (
              <VolumeMuteRounded fontSize="inherit" />
            ) : (
              <VolumeUpRounded fontSize="inherit" />
            )}
          </IconButton>
          <Slider
            size="small"
            color="secondary"
            valueLabelDisplay="auto"
            aria-label={tButtons('volume')}
            title={tButtons('volume')}
            classes={{
              root: s['slider'],
              thumb: s['thumb'],
            }}
            min={0}
            max={1}
            step={0.01}
            value={playerState.volume}
            valueLabelFormat={(value) => formatVolume(value)}
            onChange={(e, newValue) => handleVolume(Number(newValue))}
          />
        </div>
      )}
      <audio
        ref={setAudioRef}
        src={player.currentTrack?.fullLink}
        onPlay={handlePlay}
        onPause={handlePause}
        onTimeUpdate={onTimeUpdate}
        onLoadedMetadata={onLoadedMetadata}
        onEnded={onEnded}
      />
    </div>
  );
};

export default AppPlayer;
